// src/components/ContactForm/ContactFormMobile.tsx
import React, { useState, useEffect } from 'react';
import { Send, AlertCircle, Loader2, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useContactForm from '../../../hooks/useContactForm';
import Notification from '../../misc/Notification';

export interface ContactFormProps {
  className?: string;
  title?: string;
  subtitle?: string;
}

export default function ContactFormMobile({
  className = '',
  title,
  subtitle,
}: ContactFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name:'', email:'', company:'', message:'' });
  const { loading, error, success, sendContactForm } = useContactForm();
  const [errorMessage, setErrorMessage] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);

  useEffect(() => {
    if (success) {
      setFormData({ name:'', email:'', company:'', message:'' });
      setNotificationMessage(t('contact_form.success'));
      setShowNotification(true);
    }
  }, [success, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedPolicy) {
      setErrorMessage(t('contact_form.errors.policyNotAccepted'));
      return;
    }
    try {
      await sendContactForm({
        name: formData.name,
        email: formData.email,
        company_name: formData.company,
        message: formData.message,
      });
    } catch {
      setErrorMessage(t('contact_form.error_message'));
    }
  };

  return (
    <div
      id="contact-section"
      className={`w-full max-w-lg mx-auto p-8 bg-[#101827] rounded-xl shadow-lg border border-gray-700 ${className}`}
    >
        {/* Bloque FAQs */}
      <div className="mb-6 text-center">
        <h3 className="text-xl font-bold text-white mb-1">{t('contact_form.doubts')}</h3>
        <p className="text-base text-gray-400 mb-2">{t('contact_form.faqs')}</p>
        <Link
          to="/faq"
          className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-base font-semibold"
        >
          <HelpCircle className="h-5 w-5" />
          {t('contact_form.goto_faqs')}
        </Link>
      </div>
      {/* Título/Subtítulo opcionales */}
      {title && (
        <h2 className="text-2xl font-extrabold text-white text-center mb-2">{title}</h2>
      )}
      {subtitle && (
        <p className="text-base text-center text-gray-400 mb-6">{subtitle}</p>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {['name','email','company','message'].map((field) => (
          <div key={field}>
            <label
              htmlFor={field}
              className="block text-base font-medium text-gray-300 mb-1"
            >
              {t(`contact_form.${field}`)}
            </label>
            {field !== 'message' ? (
              <input
                id={field}
                type={field==='email'?'email':'text'}
                value={formData[field as keyof typeof formData]}
                onChange={e => setFormData({ ...formData, [field]: e.target.value })}
                disabled={loading}
                required
                className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-600
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white text-base"
              />
            ) : (
              <textarea
                id="message"
                rows={4}
                value={formData.message}
                onChange={e => setFormData({ ...formData, message: e.target.value })}
                disabled={loading}
                required
                className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-600
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white text-base"
              />
            )}
          </div>
        ))}

        {/* Checkbox de política */}
        <div className="flex items-center">
          <input
            id="privacyPolicy"
            type="checkbox"
            checked={acceptedPolicy}
            onChange={e => setAcceptedPolicy(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="privacyPolicy" className="ml-2 text-base text-gray-300">
            {t('contact_form.acceptPolicy')}{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
              {t('contact_form.privacyPolicy')}
            </a>
          </label>
        </div>

        {/* Errores */}
        {(errorMessage || error) && (
          <div className="text-red-500 text-base flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {error || errorMessage}
          </div>
        )}

        {/* Botón enviar */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-3
                     rounded-lg text-base font-semibold flex items-center justify-center gap-2
                     transition-all shadow-lg hover:scale-105 hover:shadow-xl
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              {t('contact_form.processing')}
              <Loader2 className="h-5 w-5 animate-spin" />
            </>
          ) : (
            <>
              {t('contact_form.submit')}
              <Send className="h-5 w-5" />
            </>
          )}
        </button>

        {success && (
          <p className="text-base text-center text-green-400 mt-4">
            {t('contact_form.success')}
          </p>
        )}
      </form>

      {/* Notificación */}
      {showNotification && (
        <Notification
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}
    </div>
  );
}
